/* RelaxPals: soft cross-fades on top of Horizon (uploaded by `deploy.py theme`, included from layout/theme.liquid).
   1. Product cards: Horizon jumps to the second photo on hover. A still copy of the outgoing photo is laid over the card and
      dissolved once the incoming photo is ready, so the swap reads as a cross-fade.
   2. Product page and quick add: picking a colour or pack replaces the whole gallery. The outgoing photo is held over the new
      gallery the same way and dissolved into the new colour. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FADE_MS = 450;

  /** Lay a still copy of `img` exactly over it, inside `host` (a positioned ancestor). */
  function ghost(img, host) {
    if (!img || !host || !img.complete || !img.naturalWidth) return null;
    const ir = img.getBoundingClientRect();
    const hr = host.getBoundingClientRect();
    if (ir.width < 2 || ir.height < 2) return null;
    const cs = getComputedStyle(img);
    const copy = document.createElement('img');
    copy.className = 'rp-ghost';
    copy.alt = '';
    copy.setAttribute('aria-hidden', 'true');
    copy.decoding = 'sync';
    copy.src = img.currentSrc || img.src;
    Object.assign(copy.style, {
      left: `${ir.left - hr.left - host.clientLeft}px`,
      top: `${ir.top - hr.top - host.clientTop}px`,
      width: `${ir.width}px`,
      height: `${ir.height}px`,
      objectFit: cs.objectFit,
      objectPosition: cs.objectPosition,
      borderRadius: cs.borderRadius,
    });
    host.appendChild(copy);
    return copy;
  }

  /** Resolves once `img` has pixels (or after `timeout` ms). */
  function ready(img, timeout) {
    if (!img || (img.complete && img.naturalWidth)) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, timeout);
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  function dissolve(copy, when) {
    when.then(() =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          copy.style.opacity = '0';
          setTimeout(() => copy.remove(), FADE_MS + 150);
        })
      )
    );
  }

  // 1. product cards ---------------------------------------------------------------------------
  function cardImage(gallery) {
    const show = gallery.querySelector('slideshow-component');
    const slide = show && show.slides && typeof show.current === 'number' ? show.slides[show.current] : null;
    return (slide || gallery).querySelector('img');
  }

  function onCardPointer(event) {
    if (reduce.matches || event.pointerType !== 'mouse') return;
    const gallery = event.target;
    if (!(gallery instanceof Element) || !gallery.classList.contains('card-gallery')) return;
    if (!gallery.hasAttribute('on:pointerenter')) return; // single-photo cards never swap
    const copy = ghost(cardImage(gallery), gallery.querySelector('slideshow-container') || gallery);
    if (!copy) return;
    // capture phase on window runs before Horizon's own hover handler, which swaps the slide synchronously after us
    requestAnimationFrame(() => dissolve(copy, ready(cardImage(gallery), 800)));
  }

  window.addEventListener('pointerenter', onCardPointer, true);
  window.addEventListener('pointerleave', onCardPointer, true);

  // 2. product page / quick add gallery ---------------------------------------------------------
  function firstVisibleImage(root) {
    for (const img of root.querySelectorAll('img')) {
      const r = img.getBoundingClientRect();
      if (r.width > 40 && r.height > 40 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth) {
        return img;
      }
    }
    return null;
  }

  document.addEventListener(
    'change',
    (event) => {
      if (reduce.matches) return;
      const input = event.target;
      if (!(input instanceof Element) || !input.closest('variant-picker')) return;
      const scope = input.closest('.shopify-section, dialog');
      const gallery = scope && scope.querySelector('media-gallery');
      const host = gallery && gallery.parentElement;
      if (!host) return;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      const copy = ghost(firstVisibleImage(gallery), host);
      if (!copy) return;
      copy.classList.add('rp-ghost--over'); // media-gallery is sticky with its own z-index; the copy must sit above it
      const started = performance.now();
      (function waitForNewGallery() {
        const current = host.querySelector(':scope > media-gallery');
        if (current && current !== gallery) {
          dissolve(copy, ready(firstVisibleImage(current) || current.querySelector('img'), 1500));
        } else if (performance.now() - started > 4000) {
          copy.remove();
        } else {
          requestAnimationFrame(waitForNewGallery);
        }
      })();
    },
    true
  );
})();
